import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabaseClient';

const LoginForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [isResetOpen, setIsResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetError, setResetError] = useState('');
  const [isResetSending, setIsResetSending] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const openReset = () => {
    setResetError('');
    setResetEmail(formData?.email || '');
    setIsResetOpen(true);
  };

  const sendResetEmail = async () => {
    const email = resetEmail?.trim();
    if (!email) {
      setResetError('이메일을 입력해주세요');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setResetError('올바른 이메일 형식이 아닙니다');
      return;
    }

    setIsResetSending(true);
    setResetError('');
    try {
      const redirectTo = `${window.location.origin}/reset-password`;
      const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
      if (error) {
        setResetError(error.message || '비밀번호 재설정 이메일 발송에 실패했습니다');
        return;
      }

      alert('비밀번호 재설정 링크를 이메일로 발송했습니다. 메일함을 확인해주세요.');
      setIsResetOpen(false);
    } finally {
      setIsResetSending(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = '이메일을 입력해주세요';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = '올바른 이메일 형식이 아닙니다';
    }

    if (!formData?.password) {
      newErrors.password = '비밀번호를 입력해주세요';
    } else if (formData?.password?.length < 6) {
      newErrors.password = '비밀번호는 최소 6자 이상이어야 합니다';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    const { data, error } = await supabase.auth.signInWithPassword({
      email: formData?.email,
      password: formData?.password,
    });

    if (error) {
      setErrors({
        email: '이메일 또는 비밀번호가 올바르지 않습니다',
        password: '이메일 또는 비밀번호가 올바르지 않습니다',
      });
      setIsLoading(false);
      return;
    }

    const authedUser = data?.user;
    if (authedUser?.id) {
      try {
        const usernameFromMeta = authedUser?.user_metadata?.username;
        const fallbackUsername = (authedUser?.email || '')?.split('@')?.[0] || 'user';
        const username = (usernameFromMeta || fallbackUsername || 'user')?.slice(0, 20);

        const { error: profileError } = await supabase
          .from('ch_profiles')
          .upsert(
            {
              id: authedUser.id,
              username,
              role: 'user',
            },
            { ignoreDuplicates: true }
          );

        if (profileError) {
          console.warn('[LoginForm] ch_profiles upsert failed', profileError);
        }
      } catch (e2) {
        console.warn('[LoginForm] ch_profiles upsert unexpected error', e2);
      }
    }

    navigate('/project-dashboard');
    setIsLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
      <Input
        label="이메일"
        type="email"
        name="email"
        placeholder="example@challengearchive.com"
        value={formData?.email}
        onChange={handleChange}
        error={errors?.email}
        required
      />
      <div className="relative">
        <Input
          label="비밀번호"
          type={showPassword ? 'text' : 'password'}
          name="password"
          placeholder="비밀번호를 입력하세요"
          value={formData?.password}
          onChange={handleChange}
          error={errors?.password}
          required
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-gaming"
          aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
        >
          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
        </button>
      </div>
      <div className="flex items-center justify-between text-sm">
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-border bg-background text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background transition-gaming"
          />
          <span className="text-muted-foreground">로그인 상태 유지</span>
        </label>
        <button
          type="button"
          className="text-primary hover:text-primary/80 transition-gaming font-medium"
          onClick={openReset}
        >
          비밀번호 찾기
        </button>
      </div>
      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={isLoading}
        iconName="LogIn"
        iconPosition="right"
        className="mt-6 md:mt-8"
      >
        로그인
      </Button>

      {isResetOpen && (
        <div className="fixed inset-0 z-dialog bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-card rounded-lg border border-border shadow-gaming-lg p-4 md:p-6">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="text-lg font-heading font-semibold text-foreground">비밀번호 찾기</div>
                <div className="text-xs md:text-sm text-muted-foreground caption mt-1">
                  가입한 이메일로 재설정 링크를 보내드립니다.
                </div>
              </div>
              <button
                type="button"
                onClick={() => !isResetSending && setIsResetOpen(false)}
                className="p-2 rounded-md hover:bg-muted transition-gaming text-muted-foreground"
              >
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="space-y-4">
              <Input
                label="이메일"
                type="email"
                placeholder="example@challengearchive.com"
                value={resetEmail}
                onChange={(e) => setResetEmail(e?.target?.value)}
                error={resetError}
                required
              />

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button variant="outline" type="button" onClick={() => setIsResetOpen(false)} disabled={isResetSending}>
                  취소
                </Button>
                <Button type="button" variant="default" onClick={sendResetEmail} loading={isResetSending}>
                  링크 보내기
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default LoginForm;